import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Package, Tags, DollarSign, Truck, Star, AlertTriangle, Edit, ExternalLink, MapPin } from 'lucide-react';
import { useInventory } from '../context/InventoryContext';
import ProductLocationManager from '../components/locations/ProductLocationManager';
import ProductForm from '../components/products/ProductForm';
import AllocationBadges from '../components/products/AllocationBadges';
import Button from '../components/ui/Button';
import Badge from '../components/ui/Badge';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { supabase } from '../lib/supabase';
import { SupplyProductAllocation } from '../types';

const ProductDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getProductById, getCategoryName, isLowStock, products, getProductTotalStock, getProductLocations } = useInventory();
  const [showEditForm, setShowEditForm] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [allocations, setAllocations] = useState<SupplyProductAllocation[]>([]);
  const [loadingAllocations, setLoadingAllocations] = useState(true);
  
  const product = id ? getProductById(id) : undefined;
  const isNewProduct = id === 'new';
  const totalStock = product ? getProductTotalStock(product.id) : 0;
  const productLocations = product ? getProductLocations(product.id) : [];

  useEffect(() => {
    if (product?.id) {
      fetchAllocations();
    }
  }, [product?.id]);

  const fetchAllocations = async () => {
    if (!product?.id) return;

    try {
      setLoadingAllocations(true);
      const { data, error } = await supabase
        .from('supply_product_allocations')
        .select('*, manufactured_product:manufactured_products(id, name, sku, status)')
        .eq('supply_id', product.id)
        .eq('is_active', true);

      if (error) throw error;
      setAllocations(data || []);
    } catch (error) {
      console.error('Error fetching allocations:', error);
      setAllocations([]);
    } finally {
      setLoadingAllocations(false);
    }
  };

  const handlePurchase = async () => {
    if (!product?.purchase_url) return;

    try {
      setIsPurchasing(true);

      // Track the click event (you can implement your analytics here)
      console.log('Purchase button clicked:', {
        productId: product.id,
        productName: product.name,
        purchaseUrl: product.purchase_url
      });

      // Open the purchase URL in a new tab
      window.open(product.purchase_url, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error processing purchase:', error);
    } finally {
      setIsPurchasing(false);
    }
  };

  if (!isNewProduct && !product) {
    return (
      <div className="text-center py-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Supply Not Found</h2>
        <p className="text-gray-500 mb-6">The supply you're looking for doesn't exist.</p>
        <Button onClick={() => navigate('/supplies')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Supplies
        </Button>
      </div>
    );
  }

  if (isNewProduct || showEditForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/supplies')}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-gray-900">
            {isNewProduct ? 'Add New Supply' : `Edit Supply: ${product?.name}`}
          </h1>
        </div>
        
        <ProductForm
          key={isNewProduct ? 'new' : product?.id}
          product={product}
          isEdit={!isNewProduct}
        />
      </div>
    );
  }

  // Find related products in the same category
  const relatedProducts = products
    .filter(p => p.category_id === product?.category_id && p.id !== product?.id)
    .slice(0, 3);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/supplies')}>
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back
          </Button>
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <Link to="/supplies" className="text-sm text-gray-500 hover:text-gray-700">
                  Supplies
                </Link>
              </li>
              <li>
                <span className="text-sm text-gray-600">/</span>
              </li>
              <li>
                <span className="text-sm text-gray-900">{product?.name}</span>
              </li>
            </ol>
          </nav>
        </div>
        <Button onClick={() => setShowEditForm(true)}>
          <Edit className="h-4 w-4 mr-2" />
          Edit Supply
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="aspect-w-16 aspect-h-9 mb-6">
              {product?.image ? (
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-64 object-contain bg-gray-50 rounded-lg"
                />
              ) : (
                <div className="w-full h-64 bg-gray-100 rounded-lg flex items-center justify-center">
                  <Package className="h-12 w-12 text-gray-600" />
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h1 className="text-2xl font-bold text-gray-900">{product?.name}</h1>
                <Badge variant={isLowStock(product!) ? 'danger' : 'success'}>
                  {isLowStock(product!) ? 'Low Stock' : 'In Stock'}
                </Badge>
              </div>

              <div className="flex items-center gap-2">
                <Badge variant="secondary">
                  {getCategoryName(product?.category_id || '')}
                </Badge>
                <span className="text-sm text-gray-500">SKU: {product?.sku}</span>
              </div>

              <p className="text-gray-600">{product?.description}</p>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-600 mb-1">
                    <DollarSign className="h-4 w-4" />
                    <span className="text-sm">Price</span>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">
                    ${product?.price.toFixed(2)}
                  </span>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-600 mb-1">
                    <Package className="h-4 w-4" />
                    <span className="text-sm">Stock</span>
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-gray-900">
                      {totalStock}
                    </span>
                    <span className="text-sm text-gray-600">
                      {product?.unit_of_measure}
                    </span>
                  </div>
                </div>
              </div>

              {/* Location Stock Breakdown */}
              {productLocations.length > 0 && (
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Stock by Location</h4>
                  <div className="space-y-2">
                    {productLocations.map((pl) => (
                      <div key={pl.id} className="flex items-center justify-between">
                        <div className="flex items-center">
                          <MapPin className="h-4 w-4 mr-2 text-gray-500" />
                          <span className="text-sm text-gray-700">{pl.location?.name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-gray-900">
                            {pl.quantity} {product?.unit_of_measure}
                          </span>
                          {pl.quantity <= pl.min_stock_level && (
                            <Badge variant="warning" className="text-xs">Low</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {product?.purchase_url && (
                <Button
                  onClick={handlePurchase}
                  isLoading={isPurchasing}
                  fullWidth
                  aria-label={`Purchase ${product.name} from supplier`}
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Purchase from Supplier
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        <ProductLocationManager 
          product={product} 
          onStockUpdate={() => {
            // Refresh product data if needed
            window.location.reload();
          }}
        />

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Supply Details</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="divide-y divide-gray-200">
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Category</dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    {getCategoryName(product?.category_id || '')}
                  </dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">SKU</dt>
                  <dd className="text-sm text-gray-900 col-span-2">{product?.sku}</dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Primary Location</dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    {productLocations.length > 0 
                      ? productLocations[0].location?.name 
                      : product?.location || 'Not set'
                    }
                  </dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Cost Price</dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    ${product?.cost_price.toFixed(2)}
                  </dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Min Stock Level</dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    {product?.min_stock_level} {product?.unit_of_measure}
                  </dd>
                </div>
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Unit of Measure</dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    {product?.unit_of_measure}
                  </dd>
                </div>
                {product?.lot_number && (
                  <div className="py-3 grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-gray-500">Lot Number</dt>
                    <dd className="text-sm text-gray-900 col-span-2">
                      {product.lot_number}
                    </dd>
                  </div>
                )}
                {product?.expiration_date && (
                  <div className="py-3 grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-gray-500">Expiration Date</dt>
                    <dd className="text-sm text-gray-900 col-span-2">
                      {new Date(product.expiration_date).toLocaleDateString()}
                    </dd>
                  </div>
                )}
                <div className="py-3 grid grid-cols-3 gap-4">
                  <dt className="text-sm font-medium text-gray-500">Product Allocation</dt>
                  <dd className="text-sm text-gray-900 col-span-2">
                    {loadingAllocations ? (
                      <span className="text-gray-600">Loading...</span>
                    ) : allocations.length > 0 ? (
                      <AllocationBadges allocations={allocations} maxVisible={3} />
                    ) : (
                      <span className="text-gray-600">Not allocated to any products</span>
                    )}
                  </dd>
                </div>
                {product?.purchase_url && (
                  <div className="py-3 grid grid-cols-3 gap-4">
                    <dt className="text-sm font-medium text-gray-500">Purchase URL</dt>
                    <dd className="text-sm text-blue-600 hover:text-blue-800 col-span-2">
                      <a 
                        href={product.purchase_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center"
                      >
                        View Supplier Page
                        <ExternalLink className="h-4 w-4 ml-1" />
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
            </CardContent>
          </Card>

          {isLowStock(product!) && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3 text-amber-600">
                  <AlertTriangle className="h-5 w-5" />
                  <div>
                    <p className="font-medium">Low Stock Alert</p>
                    <p className="text-sm">
                      Current total stock ({totalStock} {product?.unit_of_measure}) is below minimum level ({product?.min_stock_level} {product?.unit_of_measure})
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {relatedProducts.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Related Supplies</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {relatedProducts.map(relatedProduct => (
                    <Link
                      key={relatedProduct.id}
                      to={`/products/${relatedProduct.id}`}
                      className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50"
                    >
                      <div className="w-16 h-16 bg-gray-100 rounded flex items-center justify-center">
                        {relatedProduct.image ? (
                          <img
                            src={relatedProduct.image}
                            alt={relatedProduct.name}
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <Package className="h-6 w-6 text-gray-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <h4 className="text-sm font-medium text-gray-900">
                          {relatedProduct.name}
                        </h4>
                        <p className="text-sm text-gray-500">
                          ${relatedProduct.price.toFixed(2)}
                        </p>
                      </div>
                      <Badge variant={isLowStock(relatedProduct) ? 'danger' : 'success'}>
                        {getProductTotalStock(relatedProduct.id)} {relatedProduct.unit_of_measure}
                      </Badge>
                    </Link>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;